// @flow
import React, { useState, useEffect, useReducer } from 'react';
import useSWR from 'swr';
import ApiCall from '~/_base/functions/apicalls';
import api from '~/_base/constants/api';
import ApiAddresses from '~/_base/constants/apiAddresses';
import {
  DialogTitle,
  DialogContent,
  Dialog,
  Typography,
  DialogActions,
  Tab,
  Tabs,
  Switch,
  Button,
} from '@mui/material';
import { ILoader } from '~/_base/components/helixIcons';
import ChangeLog from '~/_base/components/changeLog';
import Diagnostics from '~/_base/components/diagnostics';
import Policy from '~/_base/classes/policy';
import MaterialFormGrid, {
  MaterialFormGridControl,
} from '~/_base/components/materialFormGrid';
import { useToast } from '~/_base/context/toastContext';
import makeStyles from '@mui/styles/makeStyles';
import { t } from 'i18next';

type Props = {
  onSave: () => void,
  onClose: () => void,
  open: boolean,
  id: ?number,
};

function projectReducer(state, action) {
  switch (action.type) {
    case 'CLEAR_PROJECT_STATE':
      return {
        projectObject: undefined,
        projectPolicy: undefined,
        projectChangeLog: undefined,
      };
    case 'UPDATE_PROJECT_OBJECT':
      return {
        ...state,
        projectObject: { ...state.projectObject, ...action.payload },
      };
    case 'UPDATE_PROJECT':
      return {
        ...state,
        ...action.payload,
      };
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

export default function ProjectDialog(props: Props) {
  const classes = useStyles();
  const { data: currentContext } = useSWR(
    api.currentContext.address,
    api.currentContext.request
  );
  const { errorToast, successToast, warningToast } = useToast();
  const [gettingData, setGettingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [users, setUsers] = useState();
  const [selectedTab, setSelectedTab] = useState(0);
  const [projectState, projectDispatch] = useReducer(projectReducer, {
    projectObject: undefined,
    projectPolicy: undefined,
    projectChangeLog: undefined,
  });
  const [shouldValidate, setShouldValidate] = useState(false);

  useEffect(() => {
    async function getProject(id: ?number) {
      let usersAddress = ApiAddresses({ name: 'users_list' });
      const usersAddressCall = ApiCall(usersAddress, 'GET').then(json => {
        if (json.notice) {
          successToast(json.notice);
        }
        if (json.alert) {
          warningToast(json.alert);
        }
        setUsers(
          json.items
            .filter(item => item.active === true)
            .map(item => {
              return { key: item.id, value: item.name };
            })
        );
      });
      const isNew = id == null;
      const projectAddress = ApiAddresses({
        name: 'project_data',
        id: isNew ? 'new' : id,
      });
      const projectAddressCall = ApiCall(projectAddress, 'GET').then(json => {
        if (json.notice) {
          successToast(json.notice);
        }
        if (json.alert) {
          warningToast(json.alert);
        }

        // Initialize "managed" value in case of returning null
        if (json.entity?.managed === null) {
          json.entity.managed = false;
        }

        projectDispatch({
          type: 'UPDATE_PROJECT',
          payload: {
            projectObject: json.entity,
            projectPolicy: json.policy,
            projectChangeLog: json.change_log,
          },
        });
      });
      try {
        await Promise.all([usersAddressCall, projectAddressCall]);
      } catch (err) {
        errorToast(err.message);
      }
      setGettingData(false);
    }

    props.open && getProject(props.id);
  }, [errorToast, props.id, props.open, successToast, warningToast]);

  function updateProject(payload: Object) {
    if (!projectState.projectObject) return;
    setDirty(true);
    projectDispatch({
      type: 'UPDATE_PROJECT_OBJECT',
      payload,
    });
  }

  async function saveChange() {
    setShouldValidate(true);

    let hasErrors = !Policy.isValid(
      projectState.projectObject,
      projectState.projectPolicy
    );
    if (hasErrors) {
      warningToast(t('Please fix errors before saving'));
    } else {
      setSaving(true);
      let isNew = props.id == null;
      let body = JSON.stringify(projectState.projectObject);
      let address = ApiAddresses({
        name: 'project_save',
        id: isNew ? '' : props.id,
      });
      let method = isNew ? 'POST' : 'PATCH';
      try {
        const json = await ApiCall(address, method, null, body);
        if (json.notice) {
          successToast(json.notice);
        }
        if (json.alert) {
          warningToast(json.alert);
        }
        setSaving(false);
        setShouldValidate(false);
        props.onSave();
      } catch (err) {
        errorToast(err.message);
        setSaving(false);
        setShouldValidate(false);
      }
    }
  }

  // Start: Validate "Managed by TWMS"
  const isControlsDisabled =
    currentContext.current_user.id !== 1 &&
    (projectState.projectObject || {}).managed;

  const isShowManagedByTWMS =
    (currentContext.current_user.id === 1 ||
      (projectState.projectObject || {}).managed);
  // End

  const isNew = props.id == null;
  const showChangeLog = Policy.setting(
    projectState.projectPolicy,
    'change_log?'
  );
  const showDiagnostics = Policy.setting(
    projectState.projectPolicy,
    'change_log_with_links?'
  );
  const reportingProjectsEnabled = Policy.setting(
    projectState.projectPolicy,
    'reporting_projects_enabled?'
  );

  const bindingProps = {
    source: projectState.projectObject,
    policy: projectState.projectPolicy,
  };

  return (
    <Dialog
      data-testid="project_dialog"
      open={props.open}
      onClose={e => {
        projectDispatch({ type: 'CLEAR_PROJECT_STATE' });
        setSelectedTab(0);
        props.onClose();
      }}
      PaperProps={{
        style: {
          width: '90vw',
          maxWidth: '600px',
          height: '90vh',
          maxHeight: '800px',
        },
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <div style={{ display: 'flex' }}>
          <Typography variant="h3" className={classes.title}>
            {isNew ? t('Create Project') : t('Edit Project')}

            {isControlsDisabled ? (
              <div>
                <Typography
                  variant="subtitle2"
                  style={{ fontSize: 12, color: '#d93240', marginTop: 10 }}
                >
                  {t(
                    'Editing not available: this project is being managed by TWMS'
                  )}
                </Typography>
              </div>
            ) : null}
          </Typography>
          <div className={classes.titleActionContainer}>
            {isShowManagedByTWMS ? (
              <MaterialFormGridControl
                {...bindingProps}
                prop="managed"
                label={<span>{t('Managed')}</span>}
                switchComponent={<Switch color="primary" />}
                disabled={
                  isControlsDisabled || currentContext.current_user.id !== 1
                }
                tooltip={t('Managed by TWMS')}
                onChange={(prop, val) =>
                  val != null ? updateProject({ [prop]: val }) : undefined
                }
              />
            ) : null}

            <MaterialFormGridControl
              {...bindingProps}
              prop="active"
              label={<span>{t('Active')}</span>}
              switchComponent={<Switch color="primary" />}
              onChange={(prop, val) => updateProject({ [prop]: val })}
              disabled={isControlsDisabled}
              disabledTooltip={t(
                'Editing not available: this project is being managed by TWMS'
              )}
            />
          </div>
        </div>
        <Tabs
          value={selectedTab}
          onChange={(e, value) => setSelectedTab(value)}
        >
          <Tab
            classes={{ root: classes.tabRoot }}
            key="Details"
            label={t('Details')}
          />
          {!showChangeLog ? null : (
            <Tab
              classes={{ root: classes.tabRoot }}
              key="Change Log"
              label={t('Change Log')}
            />
          )}
          {!showDiagnostics
            ? null
            : projectState.projectPolicy && (
                <Tab
                  classes={{ root: classes.tabRoot }}
                  key="Diagnostics"
                  label={t('Diagnostics')}
                />
              )}
        </Tabs>
      </DialogTitle>
      <DialogContent classes={{ root: classes.tabContent }}>
        {gettingData ? (
          <ILoader fontSize="large" />
        ) : (
          <React.Fragment>
            {selectedTab === 0 && (
              <MaterialFormGrid
                {...bindingProps}
                onChange={(prop, val) => updateProject({ [prop]: val })}
              >
                <MaterialFormGridControl
                  shouldValidate={shouldValidate}
                  size={12}
                  label={t('Job number')}
                  prop="job_number"
                  disabled={isControlsDisabled}
                  disabledTooltip={t(
                    'Editing not available: this project is being managed by TWMS'
                  )}
                />
                <MaterialFormGridControl
                  shouldValidate={shouldValidate}
                  size={12}
                  label={t('Name')}
                  prop="name"
                  disabled={isControlsDisabled}
                  disabledTooltip={t(
                    'Editing not available: this project is being managed by TWMS'
                  )}
                />
                <MaterialFormGridControl
                  size={12}
                  label={t('Address')}
                  prop="address"
                  disabled={isControlsDisabled}
                  disabledTooltip={t(
                    'Editing not available: this project is being managed by TWMS'
                  )}
                />
                <MaterialFormGridControl
                  size={12}
                  label={t('Description')}
                  prop="description"
                  multiline={true}
                  disabled={isControlsDisabled}
                  disabledTooltip={t(
                    'Editing not available: this project is being managed by TWMS'
                  )}
                />
                <MaterialFormGridControl
                  size={12}
                  label={t('Owner')}
                  prop="user_id"
                  options={users}
                  disabled={isControlsDisabled}
                  disabledTooltip={t(
                    'Editing not available: this project is being managed by TWMS'
                  )}
                />
                {reportingProjectsEnabled ? (
                  <MaterialFormGridControl
                    size={12}
                    label={t('Reporting Project')}
                    prop="inherit_wbs_items"
                    helpKey="reporting_project"
                  />
                ) : null}
              </MaterialFormGrid>
            )}
            {selectedTab === 1 && (
              <ChangeLog
                changeLog={projectState.projectChangeLog}
                generateLinks={showDiagnostics}
              />
            )}
            {selectedTab === 2 && (
              <Diagnostics
                record={projectState.projectObject}
                policy={projectState.projectPolicy}
              />
            )}
          </React.Fragment>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            props.onClose();
            setShouldValidate(false);
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          sx={{ minWidth: '100px' }}
          startIcon={
            saving && (
              <ILoader fontSize="small" />
            )
          }
          disabled={!dirty}
          variant="contained"
          color="primary"
          onClick={e => saveChange()}
        >
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    paddingLeft: '0',
    paddingRight: '0',
    paddingBottom: '0',
  },
  dialogActions: {
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  title: {
    flex: 1,
    paddingLeft: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  titleActionContainer: {
    right: theme.spacing(2),
    top: theme.spacing(2),
  },
  tabRoot: {
    minWidth: 0,
  },
  tabContent: {
    borderTop: '1px solid #e6eaec',
    marginTop: '-1px',
  },
}));
